import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import React, { useEffect } from 'react';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  extraToolbarItems?: React.ReactNode;
}

export function RichTextEditor({ content, onChange, placeholder, extraToolbarItems }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[200px] px-3 py-2 text-[#EBFFEE]',
        'data-placeholder': placeholder || '',
      },
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border border-[#504A4A] rounded-md bg-[#363232] overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 border-b border-[#504A4A] bg-[#221F1F]">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive('bold') ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive('italic') ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive('strike') ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
        >
          <s>S</s>
        </button>
        <div className="w-px h-6 bg-[#504A4A] mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive('heading', { level: 1 }) ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
        >
          H1
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive('heading', { level: 2 }) ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive('heading', { level: 3 }) ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
        >
          H3
        </button>
        <div className="w-px h-6 bg-[#504A4A] mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive('bulletList') ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
        >
          &bull;
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive('orderedList') ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
        >
          1.
        </button>
        <div className="w-px h-6 bg-[#504A4A] mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive({ textAlign: 'left' }) ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
          title="Align Left"
        >
          &#x2B05;
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive({ textAlign: 'center' }) ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
          title="Align Center"
        >
          &#x2B0C;
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`px-2 py-1 rounded text-sm ${
            editor.isActive({ textAlign: 'right' }) ? 'bg-[#9B1421] text-[#EBFFEE]' : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
          }`}
          title="Align Right"
        >
          &#x27A1;
        </button>
        <div className="w-px h-6 bg-[#504A4A] mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          className="px-2 py-1 rounded text-sm bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]"
        >
          Clear
        </button>
        {extraToolbarItems}
      </div>
      {/* Editor Content */}
      <div className="p-3">
        <EditorContent editor={editor} />
      </div>
      <style>{`
        .ProseMirror {
          outline: none;
          min-height: 200px;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #A9A3A3;
          pointer-events: none;
          height: 0;
        }
        .ProseMirror p {
          margin: 0.5em 0;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ProseMirror h1, .ProseMirror h2, .ProseMirror h3 {
          margin: 0.75em 0 0.5em 0;
          font-weight: bold;
        }
        .ProseMirror h1 {
          font-size: 1.5em;
        }
        .ProseMirror h2 {
          font-size: 1.25em;
        }
        .ProseMirror h3 {
          font-size: 1.1em;
        }
        .ProseMirror a {
          color: #E8616C;
          text-decoration: underline;
        }
        .ProseMirror strong {
          font-weight: bold;
        }
        .ProseMirror em {
          font-style: italic;
        }
        .ProseMirror hr {
          border: none;
          border-top: 1px solid #504A4A;
          margin: 1em 0;
        }
      `}</style>
    </div>
  );
}
